import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

/**
 * Servicio de Consumo de Autorización (BFF - Backend For Frontend).
 * * Este servicio actúa como el puente de comunicación entre la aplicación Angular y el
 * microservicio de autorización. A diferencia de un servicio de autenticación, este
 * se centra en la recuperación de capacidades (permisos) y la gestión de la seguridad
 * a nivel de sesión del navegador.
 * * ### Responsabilidades Críticas:
 * 1. **Handshake CSRF:** Inicialización del mecanismo de protección contra Cross-Site Request Forgery.
 * 2. **Context Discovery:** Recuperación del grafo completo de permisos y grupos del usuario.
 * 3. **Session Integrity:** Gestión de cabeceras de seguridad mediante el uso de `withCredentials`.
 * * @see {@link AuthService}
 * @see {@link HttpClient}
 */
@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  /** * Punto de entrada base para el microservicio de autorización.
   * Recuperado de las variables de entorno para facilitar el despliegue multi-entorno.
   */
  private apiUrl = environment.auth_environment.BFF_BACKEND_URL;

  /**
   * Inicializa el cliente HTTP.
   * @param http Cliente para la ejecución de peticiones REST.
   */
  constructor(private http: HttpClient) { }

  /**
   * Recupera el modelo consolidado de autorización para un usuario específico.
   * * El objeto retornado (`UserContext`) es esencial para que el frontend pueda
   * determinar dinámicamente qué módulos y acciones están permitidos para el usuario.
   * * @param idExternal Identificador único del usuario en el proveedor de identidad (uid de Firebase).
   * @param idp Código del proveedor de identidad original (ej. 'KEYCLOAK').
   * * @returns Un {@link Observable} que emite el {@link UserContext} con grupos, roles y permisos.
   * * @throws {HttpErrorResponse} En caso de fallos de red o errores de validación 4xx/5xx.
   */
  getUserContext(
    idExternal: string,
    idp: string
  ): Observable<any> {

    const headers = new HttpHeaders()
      .set('x-idp-code', idp);

    return this.http.get<any>(
      `${this.apiUrl}${environment.auth_environment.BFF_BACKEND_SERVICE_USER_CONTEXT}/${idExternal}`,
      {
        headers,
        withCredentials: true
      }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Procesa y normaliza los errores resultantes de las operaciones HTTP.
   * * Distingue entre errores de infraestructura (red/browser) y errores de lógica
   * de negocio devueltos por el servidor (Bad Request, Unauthorized, etc).
   * * @param error Objeto de error capturado en el stream.
   * @private
   */
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // Errores de red o del motor de ejecución del navegador
      console.error('Error de infraestructura detectado:', error.error.message);
    } else {
      // Errores controlados devueltos por el API Gateway o BFF
      console.error(
        `Backend respondió con código ${error.status}. Contenido:`,
        error.error
      );
    }

    return throwError(() =>
      error
    );
  }
}
